import React, { useState, useEffect, useContext } from 'react'
import * as R from 'ramda'
import { GET_ORDER, useInvoiceAPI } from './api'
import { InvoiceDetails, Invoiceable, InvoiceableResponse } from './model'
import PaginationUI from 'react-js-pagination'
import moment from 'moment'
import { formatCents, InvoiceableLink } from './utils'
import { usePaymentsAPI, AccountsCache, TransactionList, Transaction } from 'views/order/v5/api/payments-api'
import { Panel } from '../order/v5/fragments/comp/panel'
import { useQuery } from '@apollo/react-hooks'

import { Order } from 'views/order/v5/model-order'
import { OrdersAPI5ClientCtx } from 'context'

import { ApolloClient } from 'apollo-boost'

export default function OrdersTransactions(props: { invoiceId: number; invoiceType: string }): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [invoiceables, setInvoiceables] = useState<InvoiceableResponse | null>(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    invoiceAPI.getInvoiceables(props.invoiceId as number, page, 25).then(setInvoiceables)
  }, [props.invoiceId, page])

  const invoiceableTransactions: Invoiceable[] = invoiceables ? invoiceables.transactions : []

  return (
    <Panel id="transactions" label="Orders & Charges">
      <table className="table table-condensed table-striped">
        <thead>
          <tr>
            <th>Type</th>
            <th>Id</th>
            <th>Info</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {invoiceableTransactions.map((t: Invoiceable) => {
            switch (t.billable_type) {
              case 'order':
                return <OrderInvoiceableLine key={t.billable_id} {...t} invoiceId={props.invoiceId} />
              default:
                return <InvoiceableLine key={t.billable_id} {...t} invoiceId={props.invoiceId} />
            }
          })}
        </tbody>
      </table>
      {invoiceables && (
        <PaginationUI
          prevPageText="prev"
          nextPageText="next"
          firstPageText="first"
          lastPageText="last"
          itemsCountPerPage={10}
          activePage={invoiceables.pagination.page}
          pageRangeDisplayed={3}
          onChange={setPage}
          totalItemsCount={invoiceables.pagination.total}
          itemClass="page-item"
          linkClass="page-link"
        />
      )}
    </Panel>
  )
}

export function NonOrderInvoiceableTransactions(props: { invoiceId: number; invoiceType: string }): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [invoiceables, setInvoiceables] = useState<InvoiceableResponse | null>(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    invoiceAPI.getInvoiceablesExcept(props.invoiceId as number, 'order', page, 25).then(setInvoiceables)
  }, [props.invoiceId, page])

  const invoiceableTransactions: Invoiceable[] = invoiceables ? invoiceables.transactions : []

  if (invoiceableTransactions.length) {
    return (
      <Panel id="transactions" label="Charges">
        <table className="table table-condensed table-striped">
          <thead>
            <tr>
              <th>Type</th>
              <th>Id</th>
              <th>Info</th>
              <th></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {invoiceableTransactions.map((t: Invoiceable) => {
              return <InvoiceableLine key={t.billable_id} {...t} invoiceId={props.invoiceId} />
            })}
          </tbody>
        </table>
        {invoiceables && (
          <PaginationUI
            prevPageText="prev"
            nextPageText="next"
            firstPageText="first"
            lastPageText="last"
            itemsCountPerPage={10}
            activePage={invoiceables.pagination.page}
            pageRangeDisplayed={3}
            onChange={setPage}
            totalItemsCount={invoiceables.pagination.total}
            itemClass="page-item"
            linkClass="page-link"
          />
        )}
      </Panel>
    )
  } else return <></>
}
export function InvoiceableTransactions(props: { invoiceId: number; invoiceType: string }): JSX.Element {
  const invoiceAPI = useInvoiceAPI()
  const [invoiceables, setInvoiceables] = useState<InvoiceableResponse | null>(null)
  const [page, setPage] = useState(1)

  useEffect(() => {
    invoiceAPI.getInvoiceables(props.invoiceId as number, page, 25).then(setInvoiceables)
  }, [props.invoiceId, page])

  const invoiceableTransactions: Invoiceable[] = invoiceables ? invoiceables.transactions : []

  return (
    <Panel id="transactions" label="Orders & Charges">
      <table className="table table-condensed table-striped">
        <thead>
          <tr>
            <th>Type</th>
            <th>Id</th>
            <th>Info</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {invoiceableTransactions.map((t: Invoiceable) => {
            return <InvoiceableLine key={t.billable_id} {...t} invoiceId={props.invoiceId} />
          })}
        </tbody>
      </table>
      {invoiceables && (
        <PaginationUI
          prevPageText="prev"
          nextPageText="next"
          firstPageText="first"
          lastPageText="last"
          itemsCountPerPage={10}
          activePage={invoiceables.pagination.page}
          pageRangeDisplayed={3}
          onChange={setPage}
          totalItemsCount={invoiceables.pagination.total}
          itemClass="page-item"
          linkClass="page-link"
        />
      )}
    </Panel>
  )
}

export function InvoiceableLine(t: Invoiceable & { invoiceId: number }): JSX.Element {
  const [expanded, setExpanded] = useState(false)

  return (
    <>
      <tr key={t.billable_id}>
        <td className="text-danger" onClick={() => setExpanded(!expanded)}>
          {t.billable_type}
        </td>
        <td>
          <InvoiceableLink type={t.billable_type} id={t.billable_id}>
            {t.billable_id}
          </InvoiceableLink>
        </td>
        <td colSpan={3}>{t.count}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5}>
            <InvoiceableInfo {...t} />
          </td>
        </tr>
      )}
    </>
  )
}

function useOrderInfo(uuid: string): Order | null {
  const ordersClient = (useContext(OrdersAPI5ClientCtx) as unknown) as ApolloClient<any>
  const orderQuery = useQuery(GET_ORDER, { variables: { id: uuid }, client: ordersClient, errorPolicy: 'all' })
  return orderQuery.data ? orderQuery.data.order : null
}

export function OrderInvoiceableLine(t: Invoiceable & { invoiceId: number }): JSX.Element {
  const [expanded, setExpanded] = useState(false)
  const order = useOrderInfo(t.billable_id)

  if (!order) {
    return <InvoiceableLine {...t} />
  }

  return (
    <>
      <tr key={t.billable_id}>
        <td className="text-danger" onClick={() => setExpanded(!expanded)}>
          {t.billable_type}
        </td>
        <td>
          <InvoiceableLink type={t.billable_type} id={t.billable_id}>
            {t.billable_id.split(/-/)[0]}
          </InvoiceableLink>
        </td>
        <td>
          {order.carts.customer.name} / {order.carts.customer.email}
        </td>
        <td>{order.carts.settings.service.label}</td>
        <td>{moment(order.created_at).format('L')}</td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={5}>
            <OrderInvoiceableInfo {...t} order={order} />
          </td>
        </tr>
      )}
    </>
  )
}

function OrderInvoiceableInfo(t: Invoiceable & { invoiceId: number; order: Order }): JSX.Element {
  return <InvoiceableInfo {...t} />
}

export function InvoiceableInfo(t: Invoiceable & { invoiceId: number }): JSX.Element {
  const paymentsApi = usePaymentsAPI()
  const invoiceAPI = useInvoiceAPI()
  const [transactions, setTransactions] = useState<TransactionList | null>(null)
  const [details, setDetails] = useState<InvoiceDetails | null>(null)
  const [accounts, setAccounts] = useState<AccountsCache>({})

  useEffect(() => {
    paymentsApi.findInvoiceableTransactions(t.invoiceId, t.billable_id, t.billable_type, 1, 100).then(setTransactions)
  }, [])

  useEffect(() => {
    invoiceAPI.getInvoiceableSummary(t.invoiceId, t.billable_id, t.billable_type).then(setDetails)
  }, [])

  useEffect(() => {
    if (transactions) {
      const accs = new Set<number>()
      transactions.transactions.reduce((sum, { credit_account_id, debit_account_id }) => {
        sum.add(credit_account_id)
        sum.add(debit_account_id)
        return sum
      }, accs)
      Promise.all([...accs].map(paymentsApi.loadAccount)).then((res: Account[]) => {
        setAccounts(res.reduce((sum, a) => R.assoc('' + a.id, a, sum), {}))
      })
    }
  }, [transactions])

  if (transactions && accounts && details) {
    // const extraInfo = R.toPairs(details.group_by_billing_rules).filter(
    //   ([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.total == 0
    // )

    return (
      <div className="row-fluid">
        <InvoiceSummaryFees summary={details} />
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Debit</th>
              <th>Credit</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Rule</th>
            </tr>
          </thead>
          <tbody>
            {transactions.transactions.map((t: Transaction) => (
              <TransactionLine key={t.id} {...t} accounts={accounts} />
            ))}
          </tbody>
        </table>
      </div>
    )
  } else {
    return <div>Loading...</div>
  }
}

export function FilteredInvoiceableInfo(t: Invoiceable & { invoiceId: number }): JSX.Element {
  const paymentsApi = usePaymentsAPI()
  const invoiceAPI = useInvoiceAPI()
  const [transactions, setTransactions] = useState<TransactionList | null>(null)
  const [details, setDetails] = useState<InvoiceDetails | null>(null)
  const [accounts, setAccounts] = useState<AccountsCache>({})

  useEffect(() => {
    paymentsApi.findInvoiceableTransactions(t.invoiceId, t.billable_id, t.billable_type, 1, 100).then(setTransactions)
  }, [])

  useEffect(() => {
    invoiceAPI.getInvoiceableSummary(t.invoiceId, t.billable_id, t.billable_type).then(setDetails)
  }, [])

  useEffect(() => {
    if (transactions) {
      const accs = new Set<number>()
      transactions.transactions.reduce((sum, { credit_account_id, debit_account_id }) => {
        sum.add(credit_account_id)
        sum.add(debit_account_id)
        return sum
      }, accs)
      Promise.all([...accs].map(paymentsApi.loadAccount)).then((res: Account[]) => {
        setAccounts(res.reduce((sum, a) => R.assoc('' + a.id, a, sum), {}))
      })
    }
  }, [transactions])

  if (transactions && accounts && details) {
    // const extraInfo = R.toPairs(details.group_by_billing_rules).filter(
    //   ([_key, invoiceTotal]: [string, InvoiceTotals]) => invoiceTotal.totals.total == 0
    // )

    return (
      <div className="row-fluid">
        <InvoiceSummaryFees summary={details} />
        <table className="table table-condensed">
          <thead>
            <tr>
              <th>Debit</th>
              <th>Credit</th>
              <th>Description</th>
              <th>Amount</th>
              <th>Rule</th>
            </tr>
          </thead>
          <tbody>
            {transactions.transactions.map((t: Transaction) => (
              <TransactionLine key={t.id} {...t} accounts={accounts} />
            ))}
          </tbody>
        </table>
      </div>
    )
  } else {
    return <div>Loading...</div>
  }
}

export function TransactionLine(t: Transaction & { accounts: AccountsCache }): JSX.Element {
  if (t.accounts['' + t.debit_account_id] && t.accounts['' + t.credit_account_id]) {
    const credit = t.accounts['' + t.credit_account_id]
    const debit = t.accounts['' + t.debit_account_id]
    return (
      <tr key={t.id} className={debit.type == 'principal' ? 'info' : 'success'}>
        <td>{debit.name}</td>
        <td>{credit.name}</td>
        <td>{t.description}</td>
        <td align="right">${(t.amount / 100).toFixed(2)}</td>
        <td>{t.billing_rule_name || ''}</td>
      </tr>
    )
  } else
    return (
      <tr key={t.id}>
        <td>...</td>
        <td>...</td>
        <td>{t.description}</td>
        <td>{formatCents(t.amount, 'en')}</td>
        <td>{t.billing_rule_name || ''}</td>
      </tr>
    )
}

function InvoiceSummaryFees(props: { summary: InvoiceDetails }): JSX.Element {
  const { group_by_billing_rules, group_by_payload_type } = props.summary

  return (
    <table className="table table-condensed">
      <thead>
        <tr>
          <th>Order Data</th>
          <th align="right">Total</th>
        </tr>
      </thead>
      <tbody>
        {R.toPairs(group_by_payload_type)
          .filter(
            ([_type, totals]) =>
              totals.principal && totals.balance && (totals.balance.total != 0 || totals.principal.total != 0)
          )
          .map(([type, totals]) => [
            <tr key={`${type}-principal`} className="info">
              <td>Paid directly for {type}</td>
              <td>{formatCents(Math.abs(totals.principal.total), 'en')}</td>
            </tr>,
            <tr key={type} className={totals.balance.total > 0 ? 'success' : 'danger'}>
              <td>
                {totals.balance.total > 0 ? 'To receive' : 'To pay'} for {type}
              </td>
              <td>{formatCents(Math.abs(totals.balance.total), 'en')}</td>
            </tr>,
          ])}
        {R.toPairs(group_by_billing_rules)
          .filter(([_type, totals]) => totals.totals.total != 0)
          .map(([type, totals]) => (
            <tr key={type} className="info">
              <td>Fees & Charges: {type}</td>
              <td>{formatCents(totals.totals.total, 'en')}</td>
            </tr>
          ))}
      </tbody>
    </table>
  )
}
